(function () {
  const initializedGalleries = new WeakSet();
  const galleryObservers = new WeakMap();
  const manualSelectionStates = new WeakMap();
  const pendingMediaSelections = new WeakMap();

  const normalizeText = (text) => String(text || "").normalize("NFKC").toLowerCase().trim();
  const compactText = (text) => normalizeText(text).replace(/[\s_-]+/g, "");
  const isColorOption = (optionName) => /color|colour|\u30AB\u30E9\u30FC|\u8272/i.test(optionName || "");

  const getAltTokens = (alt) => {
    const tokens = [alt];

    String(alt || "").split(/[#|,\u3001/\uFF0F]+/).forEach((token) => {
      const trimmedToken = token.trim();

      if (!trimmedToken) {
        return;
      }

      tokens.push(trimmedToken);
      trimmedToken.split(/[_:\uFF1A]/).forEach((tokenPart) => {
        if (tokenPart.trim()) {
          tokens.push(tokenPart.trim());
        }
      });
    });

    return [...new Set(tokens)];
  };

  const findMatchingOptionInput = (form, alt) => {
    const altTokens = getAltTokens(alt);
    let bestMatch = null;

    Array.from(form.elements)
      .filter((input) => input.matches?.("input[data-option-position][data-option-value-name]"))
      .forEach((input) => {
        const optionValue = input.dataset.optionValueName;
        const compactOptionValue = compactText(optionValue);

        if (!compactOptionValue || input.disabled) {
          return;
        }

        for (const token of altTokens) {
          const normalizedToken = normalizeText(token);
          const compactToken = compactText(token);
          let score = 0;

          if (normalizedToken === normalizeText(optionValue)) {
            score = 1000;
          } else if (compactToken === compactOptionValue) {
            score = 900;
          } else if (compactOptionValue.length >= 2 && compactToken.includes(compactOptionValue)) {
            score = 500;
          }

          if (score === 0) {
            continue;
          }

          score += compactOptionValue.length;

          if (isColorOption(input.dataset.optionName)) {
            score += 50;
          }

          if (!bestMatch || score > bestMatch.score) {
            bestMatch = { input, score };
          }
        }
      });

    return bestMatch?.input || null;
  };

  const syncVariantPickerWithMedia = (gallery, media) => {
    const image = media?.querySelector("img[alt]");
    const form = gallery ? document.forms[gallery.getAttribute("form")] : null;
    const mediaId = media?.getAttribute("data-media-id");
    const pendingSelection = pendingMediaSelections.get(gallery);

    if (!image?.alt || !form) {
      return;
    }

    if (pendingSelection && pendingSelection.mediaId !== mediaId && pendingSelection.expiresAt > Date.now()) {
      return;
    }

    if (manualSelectionStates.get(gallery)?.isLocked) {
      return;
    }

    const input = findMatchingOptionInput(form, image.alt);

    if (!input || input.checked) {
      return;
    }

    pendingMediaSelections.set(gallery, {
      mediaId,
      expiresAt: Date.now() + 3000,
      source: pendingSelection?.mediaId === mediaId ? pendingSelection.source : "observer"
    });
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const syncNavigationWithMedia = (gallery, mediaId) => {
    gallery.querySelectorAll("product-gallery-navigation").forEach((navigation) => {
      navigation.querySelectorAll("button").forEach((button) => {
        button.setAttribute("aria-current", button.getAttribute("data-media-id") === mediaId ? "true" : "false");
      });
    });
  };

  const restoreGalleryMedia = (gallery, mediaId) => {
    const carousel = gallery.querySelector(".product-gallery__carousel");
    const media = carousel?.querySelector(`.product-gallery__media[data-media-id="${mediaId}"]:not([hidden])`);

    if (!carousel || !media) {
      return;
    }

    syncNavigationWithMedia(gallery, mediaId);

    if (carousel.isScrollable) {
      const index = carousel.cells.indexOf(media);

      if (index >= 0) {
        carousel.select(index);
      }
    } else {
      media.scrollIntoView({ block: "start", inline: "nearest", behavior: "smooth" });
    }
  };

  const scheduleGalleryMediaRestore = (gallery, mediaId) => {
    [0, 50, 150, 350, 700].forEach((delay) => {
      window.setTimeout(() => restoreGalleryMedia(gallery, mediaId), delay);
    });
  };

  const getVariantInputFromEvent = (event, formId) => {
    const target = event.target;

    if (!target?.closest) {
      return null;
    }

    const directInput = target.matches?.(`input[data-option-position][form="${formId}"]`) ? target : null;
    const labelControl = target.closest(`label`)?.control;
    const labelledInput = labelControl?.matches?.(`input[data-option-position][form="${formId}"]`) ? labelControl : null;

    return directInput || labelledInput;
  };

  const observeStaticGallery = (gallery) => {
    const carousel = gallery.querySelector(".product-gallery__carousel");

    if (!carousel || carousel.isScrollable || galleryObservers.has(gallery)) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const visibleEntry = entries
        .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (visibleEntry) {
        syncVariantPickerWithMedia(gallery, visibleEntry.target);
      }
    }, { threshold: [0, 0.5, 0.75, 1] });

    carousel.querySelectorAll(".product-gallery__media").forEach((media) => observer.observe(media));
    galleryObservers.set(gallery, observer);
  };

  const initGallery = (gallery) => {
    if (initializedGalleries.has(gallery)) {
      return;
    }

    const carousel = gallery.querySelector(".product-gallery__carousel");
    const form = document.forms[gallery.getAttribute("form")];
    const formId = gallery.getAttribute("form");

    if (!carousel || !form) {
      return;
    }

    initializedGalleries.add(gallery);

    const rememberUserSelectedMedia = (event) => {
      if (!event.isTrusted) {
        return;
      }

      const selectedMediaElement = event.target.closest?.(".product-gallery__thumbnail[data-media-id], .product-gallery__media[data-media-id]");
      const mediaId = selectedMediaElement?.getAttribute("data-media-id");

      if (!mediaId) {
        return;
      }

      manualSelectionStates.delete(gallery);
      pendingMediaSelections.set(gallery, {
        mediaId,
        expiresAt: Date.now() + 3000,
        source: "direct"
      });

      const media = carousel.querySelector(`.product-gallery__media[data-media-id="${mediaId}"]`);

      if (media) {
        window.setTimeout(() => syncVariantPickerWithMedia(gallery, media), 0);
      }
    };

    gallery.addEventListener("pointerdown", rememberUserSelectedMedia, true);
    gallery.addEventListener("click", rememberUserSelectedMedia, true);

    gallery.addEventListener("pointerdown", (event) => {
      if (event.isTrusted) {
        manualSelectionStates.delete(gallery);
      }
    });
    gallery.addEventListener("wheel", (event) => {
      if (event.isTrusted) {
        manualSelectionStates.delete(gallery);
      }
    });
    gallery.addEventListener("touchstart", (event) => {
      if (event.isTrusted) {
        manualSelectionStates.delete(gallery);
      }
    });
    gallery.addEventListener("keydown", (event) => {
      if (event.isTrusted) {
        manualSelectionStates.delete(gallery);
      }
    });

    const lockMediaSyncForManualSelection = (event) => {
      if (!event.isTrusted || !getVariantInputFromEvent(event, formId)) {
        return;
      }

      manualSelectionStates.set(gallery, {
        isLocked: true
      });
    };

    document.addEventListener("pointerdown", lockMediaSyncForManualSelection, true);
    document.addEventListener("click", lockMediaSyncForManualSelection, true);
    document.addEventListener("change", lockMediaSyncForManualSelection, true);

    carousel.addEventListener("carousel:settle", (event) => syncVariantPickerWithMedia(gallery, event.detail.cell));
    carousel.addEventListener("carousel:change", (event) => syncVariantPickerWithMedia(gallery, event.detail.cell));

    form.addEventListener("variant:change", (event) => {
      const pendingSelection = pendingMediaSelections.get(gallery);

      if (!pendingSelection || pendingSelection.expiresAt <= Date.now() || !event.detail?.variant?.featured_media) {
        return;
      }

      event.detail.previousVariant = {
        ...(event.detail.previousVariant || {}),
        featured_media: event.detail.variant.featured_media
      };
    }, true);

    form.addEventListener("product:rerender", () => {
      galleryObservers.get(gallery)?.disconnect();
      galleryObservers.delete(gallery);

      requestAnimationFrame(() => {
        const pendingSelection = pendingMediaSelections.get(gallery);
        const pendingMediaId = pendingSelection?.mediaId;

        observeStaticGallery(gallery);

        if (pendingMediaId) {
          syncNavigationWithMedia(gallery, pendingMediaId);

          if (pendingSelection.source === "direct") {
            scheduleGalleryMediaRestore(gallery, pendingMediaId);
          }
        } else {
          syncVariantPickerWithMedia(gallery, carousel.selectedCell);
        }
      });
    });

    form.addEventListener("variant:change", () => {
      const pendingSelection = pendingMediaSelections.get(gallery);
      const pendingMediaId = pendingSelection?.mediaId;

      if (!pendingMediaId) {
        return;
      }

      if (pendingSelection.source === "direct") {
        scheduleGalleryMediaRestore(gallery, pendingMediaId);
      } else {
        syncNavigationWithMedia(gallery, pendingMediaId);
      }

      window.setTimeout(() => pendingMediaSelections.delete(gallery), 1200);
    });

    observeStaticGallery(gallery);
    requestAnimationFrame(() => syncVariantPickerWithMedia(gallery, carousel.selectedCell));
  };

  const init = () => {
    document.querySelectorAll("product-gallery[form]").forEach(initGallery);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
